import { useState } from "react";
import {
  useClassesEcole,
  useNiveauxEcole,
  useListEcoleEleves,
  useCreateClasseEcole,
  useRenameClasseEcole,
  useDeleteClasseEcole,
} from "@/lib/backoffice-hooks";
import { useAnneeScope } from "@/lib/annee-scope";
import EcoleShell from "@/components/layout/EcoleShell";
import AnneeInactiveBanner from "@/components/AnneeInactiveBanner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";
import { compteDansLEffectif, type ClasseLite, type NiveauLite } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

interface GroupeClasses {
  key: string;
  libelle: string;
  classes: ClasseLite[];
}

/**
 * Page « Classes » — la structure de l'école que tout le reste consomme :
 * la grille tarifaire affiche ces classes, le formulaire élève les
 * sélectionne, les imports CSV résolvent leurs libellés. Trois gestes :
 * créer, renommer, supprimer si vide (le backend refuse — 409 — de
 * supprimer une classe où des élèves sont rattachés).
 */
export default function ClassesPage() {
  const { isActiveAnnee } = useAnneeScope();
  const { data: classes, isLoading: classesLoading } = useClassesEcole();
  const { data: niveaux, isLoading: niveauxLoading } = useNiveauxEcole();
  const { data: eleves } = useListEcoleEleves();
  const renameMutation = useRenameClasseEcole();
  const deleteMutation = useDeleteClasseEcole();

  const [createOpen, setCreateOpen] = useState(false);
  const [rename, setRename] = useState<{ classe: ClasseLite; libelle: string } | null>(null);
  const [suppression, setSuppression] = useState<ClasseLite | null>(null);

  const chargement = classesLoading || niveauxLoading;

  // Effectif par libellé de classe (le roster joint le libellé, pas l'id).
  // 21/10/2026 : seuls les inscrits comptent (règle unique, comme le tableau de
  // bord et CATHO SCHOOL PRO) ; avant, les préinscrits et les sortis
  // gonflaient l'effectif de la classe. Les autres élèves rattachés restent
  // comptés à part : une classe qui en a n'est pas vide (suppression refusée).
  const effectifs = new Map<string, number>();
  const rattaches = new Map<string, number>();
  for (const e of eleves ?? []) {
    if (!e.classe) continue;
    rattaches.set(e.classe, (rattaches.get(e.classe) ?? 0) + 1);
    if (compteDansLEffectif(e)) effectifs.set(e.classe, (effectifs.get(e.classe) ?? 0) + 1);
  }
  const effectifDe = (c: ClasseLite) => effectifs.get(c.libelle) ?? 0;
  const rattachesDe = (c: ClasseLite) => rattaches.get(c.libelle) ?? 0;

  const triClasse = (a: ClasseLite, b: ClasseLite) => a.libelle.localeCompare(b.libelle, "fr");
  const niveauxTries = [...(niveaux ?? [])].sort((a, b) => a.ordre - b.ordre);
  const idsNiveaux = new Set(niveauxTries.map((n) => n.id));
  const groupes: GroupeClasses[] = [];
  for (const n of niveauxTries) {
    const cls = (classes ?? []).filter((c) => c.niveauId === n.id).sort(triClasse);
    if (cls.length > 0) groupes.push({ key: n.id, libelle: n.libelle, classes: cls });
  }
  const orphelines = (classes ?? [])
    .filter((c) => !c.niveauId || !idsNiveaux.has(c.niveauId))
    .sort(triClasse);
  if (orphelines.length > 0) groupes.push({ key: "autres", libelle: "Autres", classes: orphelines });

  function submitRename() {
    /* v8 ignore start -- défensif : submitRename n'est appelable que depuis le dialog rendu quand rename est non nul */
    if (!rename) return;
    /* v8 ignore stop */
    renameMutation.mutate(
      { classeId: rename.classe.id, libelle: rename.libelle.trim() },
      {
        onSuccess: () => {
          toast.success(`Classe renommée en « ${rename.libelle.trim()} »`);
          setRename(null);
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  function confirmerSuppression() {
    /* v8 ignore start -- défensif : confirmerSuppression n'est appelable que depuis le dialog rendu quand suppression est non nul */
    if (!suppression) return;
    /* v8 ignore stop */
    const libelle = suppression.libelle;
    deleteMutation.mutate(suppression.id, {
      onSuccess: () => {
        toast.success(`Classe « ${libelle} » supprimée`);
        setSuppression(null);
      },
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  return (
    <EcoleShell>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-navy">Classes</h1>
          <p className="text-sm text-slate-600">
            {classes?.length ?? 0} classe(s). La grille tarifaire et le registre des élèves s'appuient dessus
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-navy hover:bg-navy/90 text-white" data-testid="button-create-classe" disabled={!isActiveAnnee}>
              <Plus className="w-4 h-4 mr-1.5" /> Nouvelle classe
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
              <DialogTitle className="font-display text-lg font-bold text-white">Créer une classe</DialogTitle>
              <DialogDescription className="text-white/70">
                Choisissez le niveau, puis le libellé exact (ex. CM1A).
              </DialogDescription>
            </DialogHeader>
            <CreateClasseForm niveaux={niveauxTries} onDone={() => setCreateOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {!isActiveAnnee && <AnneeInactiveBanner />}

      <Card className="border border-slate-200 shadow-none">
        {chargement && (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin inline text-navy" />
          </div>
        )}
        {!chargement && groupes.length === 0 && (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="empty-classes">
            Aucune classe. Créez vos classes pour pouvoir fixer les tarifs et rattacher les élèves.
          </p>
        )}
        {!chargement && groupes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Classe</th>
                  <th className="text-left">Effectif</th>
                  <th className="pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {groupes.map((g) => (
                  <GroupeRows
                    key={g.key}
                    groupe={g}
                    effectifDe={effectifDe}
                    rattachesDe={rattachesDe}
                    isActiveAnnee={isActiveAnnee}
                    onRename={(c) => setRename({ classe: c, libelle: c.libelle })}
                    onDelete={(c) => setSuppression(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Renommage */}
      <Dialog
        open={!!rename}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setRename(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Renommer la classe</DialogTitle>
            <DialogDescription>
              Le nouveau libellé est repris partout : tarifs, registre, imports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Libellé</Label>
              <Input
                value={rename?.libelle ?? ""}
                onChange={(e) => setRename((p) => p && { ...p, libelle: e.target.value })}
                data-testid="input-rename-classe"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRename(null)}>Annuler</Button>
              <Button
                onClick={submitRename}
                disabled={renameMutation.isPending || !(rename?.libelle ?? "").trim()}
                className="bg-navy text-white hover:bg-navy/90"
                data-testid="button-submit-rename"
              >
                {renameMutation.isPending ? "Enregistrement…" : "Renommer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suppression — classe vide uniquement */}
      <Dialog
        open={!!suppression}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setSuppression(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-sm" data-testid="dialog-delete-classe">
          <DialogHeader>
            <DialogTitle>Supprimer la classe</DialogTitle>
            <DialogDescription>
              Supprimer « {suppression?.libelle} » ? Ses tarifs éventuels seront supprimés avec elle.
              Une classe où des élèves sont rattachés ne peut pas être supprimée.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setSuppression(null)} data-testid="button-cancel-delete">
              Annuler
            </Button>
            <Button
              onClick={confirmerSuppression}
              disabled={deleteMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Suppression…" : "Supprimer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </EcoleShell>
  );
}

function GroupeRows({
  groupe,
  effectifDe,
  rattachesDe,
  isActiveAnnee,
  onRename,
  onDelete,
}: {
  groupe: GroupeClasses;
  effectifDe: (c: ClasseLite) => number;
  rattachesDe: (c: ClasseLite) => number;
  isActiveAnnee: boolean;
  onRename: (c: ClasseLite) => void;
  onDelete: (c: ClasseLite) => void;
}) {
  return (
    <>
      <tr className="border-t border-slate-200 bg-slate-50/60">
        <td colSpan={3} className="px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-navy" data-testid={`groupe-${groupe.libelle}`}>
          {groupe.libelle}
        </td>
      </tr>
      {groupe.classes.map((c) => {
        const effectif = effectifDe(c);
        const autres = rattachesDe(c) - effectif;
        return (
          <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-classe-${c.libelle}`}>
            <td className="px-4 py-2.5 font-medium">{c.libelle}</td>
            <td className="text-slate-600" data-testid={`effectif-${c.libelle}`}>
              {effectif} élève(s)
              {autres > 0 && (
                <span className="text-xs text-slate-400" data-testid={`hors-effectif-${c.libelle}`}>
                  {" "}· {autres} non inscrit(s)
                </span>
              )}
            </td>
            <td className="pr-4 py-1.5 text-right whitespace-nowrap">
              <button
                onClick={() => onRename(c)}
                disabled={!isActiveAnnee}
                className="text-xs px-2 py-1 mr-2 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`button-rename-${c.libelle}`}
              >
                <Pencil className="w-3 h-3" /> Renommer
              </button>
              <button
                onClick={() => onDelete(c)}
                disabled={!isActiveAnnee || rattachesDe(c) > 0}
                title={rattachesDe(c) > 0 ? "Classe non vide : détachez d'abord ses élèves" : "Supprimer cette classe vide"}
                className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`button-delete-${c.libelle}`}
              >
                <Trash2 className="w-3 h-3" /> Supprimer
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function CreateClasseForm({ niveaux, onDone }: { niveaux: NiveauLite[]; onDone: () => void }) {
  const mutation = useCreateClasseEcole();
  const [niveauId, setNiveauId] = useState("");
  const [libelle, setLibelle] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!niveauId || !libelle.trim()) {
          toast.error("Niveau et libellé sont obligatoires");
          return;
        }
        mutation.mutate(
          { niveauId, libelle: libelle.trim() },
          {
            onSuccess: (created) => {
              toast.success(`Classe « ${created.libelle} » créée`);
              onDone();
            },
            onError: (err: unknown) => toast.error(friendlyError(err)),
          },
        );
      }}
      className="space-y-3"
    >
      <div>
        <Label>Niveau *</Label>
        <select
          className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
          value={niveauId}
          onChange={(e) => setNiveauId(e.target.value)}
          data-testid="select-niveau-classe"
        >
          <option value="">Choisir</option>
          {niveaux.map((n) => (
            <option key={n.id} value={n.id}>
              {n.libelle}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label>Libellé *</Label>
        <Input
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
          placeholder="CM1A"
          data-testid="input-libelle-classe"
        />
      </div>
      <Button
        type="submit"
        disabled={mutation.isPending}
        className="w-full bg-navy hover:bg-navy/90 text-white"
        data-testid="button-submit-classe"
      >
        {mutation.isPending ? "Création…" : "Créer la classe"}
      </Button>
    </form>
  );
}
